import { ErrorMessage } from "@/components/ErrorMessage";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { EnvironmentClassesSection } from "@/components/runners/details/EnvironmentClasses";
import { LLMIntegrationSection } from "@/components/runners/details/LLMIntegration";
import { RunnerConfiguration } from "@/components/runners/details/RunnerConfiguration";
import { RunnerProviderSetup } from "@/components/runners/details/RunnerProviderSetup";
import { RunnerSharing } from "@/components/runners/details/RunnerSharing";
import { SourceControlProviderSection } from "@/components/runners/details/SourceControlProvider";
import { RunnerUsageButtons } from "@/components/runners/RunnerUsageButtons";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { TrackLocations } from "@/hooks/use-segment";
import { useRunnerConfigurationSchema } from "@/queries/runner-configuration-queries";
import type { PlainRunner } from "@/queries/runner-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { RunnerPhase, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC } from "react";

export const RunnerDetails: FC<{ runner: PlainMessage<Runner> | undefined }> = ({ runner }) => {
    const { data: schema, isLoading, isPending, error } = useRunnerConfigurationSchema(runner?.runnerId);
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();

    const schemaLoading = isLoading || isPending;
    const hasSignedUp = runner?.status?.phase !== RunnerPhase.CREATED;

    if (!hasSignedUp) {
        return (
            <div className="flex size-full flex-col gap-6" data-testid="runner-details">
                <RunnerMetadata runner={runner} isCreating />
                <RunnerProviderSetup runner={runner} hasSignedUp={false} />
            </div>
        );
    }

    return (
        <div className="flex size-full flex-col gap-6" data-testid="runner-details">
            <RunnerMetadata runner={runner} isCreating={false} />
            <RunnerProviderSetup runner={runner} hasSignedUp />
            <SkeletonBlock ready={!schemaLoading} failed={Boolean(error)} className="h-[255px]">
                <FailedToLoadSchema error={error} />
                {runner && (
                    <SourceControlProviderSection
                        runner={runner}
                        schemas={schema?.scm}
                        hasStack={hasSignedUp}
                        data-track-location={(modalState) =>
                            modalState.type == "add-provider"
                                ? TrackLocations.AddSCMProviderModal
                                : modalState.type == "remove-provider"
                                  ? TrackLocations.DeleteSCMProviderModal
                                  : TrackLocations.EditSCMProviderModal
                        }
                    />
                )}
                {runner && (
                    <LLMIntegrationSection
                        runner={runner}
                        hasStack={hasSignedUp}
                        data-track-location={(modalState) =>
                            modalState.type == "add-integration"
                                ? TrackLocations.AddLLMIntegrationModal
                                : modalState.type == "remove-integration"
                                  ? TrackLocations.DeleteLLMIntegrationModal
                                  : TrackLocations.EditLLMIntegrationModal
                        }
                    />
                )}
                {runner && (
                    <EnvironmentClassesSection
                        runner={runner}
                        schema={schema?.environmentClasses}
                        hasSignedUp={hasSignedUp}
                        data-track-location={(modalState) =>
                            modalState.type === "add"
                                ? TrackLocations.AddEnvironmentClassModal
                                : TrackLocations.EditEnvironmentClassModal
                        }
                    />
                )}
                {runner && !isGroupManagementEnabled && <RunnerSharing runner={runner} hasSignedUp={hasSignedUp} />}
                {runner && <RunnerConfiguration runner={runner} hasSignedUp={hasSignedUp} />}
            </SkeletonBlock>
        </div>
    );
};

const FailedToLoadSchema: FC<{ error: Error | null }> = ({ error }) => {
    if (!error) {
        return null;
    }
    return (
        <div className="flex flex-col items-center">
            <Text className="text-lg font-bold text-content-negative">Failed to load runner schema</Text>
            <Text className="text-base text-content-negative">
                Please try to refresh the page and contact support if the issue persists.
            </Text>
            <ErrorMessage error={error} />
        </div>
    );
};

const RunnerMetadata: FC<{
    isCreating?: boolean;
    runner?: PlainRunner | undefined;
}> = ({ runner, isCreating }) => {
    return (
        <div className="flex flex-col gap-4">
            {/* Usage buttons */}
            {runner && !isCreating && <RunnerUsageButtons runnerId={runner.runnerId} testIdPrefix="runner-details" />}
        </div>
    );
};
